import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ShieldAlert } from "lucide-react";
import { currentHomePath } from "../utils/homePath";

/**
 * AccessGuard
 * Pieza compartida de "acceso denegado": spinner mientras `loading`, los
 * `children` si `denied` es false, y si `denied` es true redirige a la home
 * del usuario — salvo que esa home sea la MISMA ruta actual (bucle infinito;
 * p. ej. un JWT sin `level`), en cuyo caso muestra un aviso inline en vez de
 * un `<Navigate>`.
 *
 * Factorizado desde `RequireAccess` para que cualquier otro guard (p. ej. uno
 * que decida por el nivel crudo del JWT en vez de por `sectionId`/`canAccess`,
 * como `RequireNotFieldVendor`) comparta el mismo aviso y la misma protección
 * anti-bucle en vez de duplicarlos.
 */
const AccessGuard = ({ loading, denied, children }) => {
  const { t } = useTranslation("common");
  const location = useLocation();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
      </div>
    );
  }

  if (!denied) {
    return children;
  }

  const target = currentHomePath();
  if (target === location.pathname) {
    return (
      <div className="flex flex-col items-center justify-center h-screen gap-3 text-center px-4">
        <ShieldAlert className="h-12 w-12 text-red-500" />
        <h1 className="text-lg font-semibold text-gray-800 dark:text-gray-100">
          {t("access_denied_title")}
        </h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 max-w-sm">
          {t("access_denied_body")}
        </p>
      </div>
    );
  }

  return <Navigate to={target} replace />;
};

export default AccessGuard;
